/**
 * InventorySystem.ts
 * Manages 64-slot inventory with stacking consumables
 */

import { ItemType } from '../lib/types/game';
import { getItem } from './data/ItemData';

export interface InventorySlot {
  itemId: number;
  quantity: number;
}

export interface UseItemResult {
  success: boolean;
  message: string;
  effect?: {
    type: 'heal' | 'restore' | 'cure_status' | 'revive' | 'party_heal';
    value: number;
    statusEffect?: number;
  };
}

/**
 * Inventory management system
 * Handles 64 slots with automatic stacking for consumables
 */
export class InventorySystem {
  private static readonly MAX_SLOTS = 64;
  private static readonly MAX_STACK = 99;

  private inventory: InventorySlot[];

  constructor() {
    this.inventory = Array(InventorySystem.MAX_SLOTS).fill(null).map(() => ({
      itemId: 0,
      quantity: 0,
    }));
  }

  /**
   * Load inventory from game state arrays
   */
  loadInventory(itemIds: number[], quantities: number[]): void {
    for (let i = 0; i < InventorySystem.MAX_SLOTS; i++) {
      this.inventory[i] = {
        itemId: itemIds[i] || 0,
        quantity: quantities[i] || 0,
      };
    }
  }

  /**
   * Export inventory to arrays for contract storage
   */
  exportInventory(): { itemIds: number[]; quantities: number[] } {
    const itemIds = this.inventory.map(slot => slot.itemId);
    const quantities = this.inventory.map(slot => slot.quantity);
    return { itemIds, quantities };
  }

  /**
   * Get all inventory slots
   */
  getInventory(): InventorySlot[] {
    return [...this.inventory];
  }

  /**
   * Get only non-empty slots
   */
  getActiveSlots(): InventorySlot[] {
    return this.inventory.filter(slot => slot.itemId > 0 && slot.quantity > 0);
  }

  /**
   * Get items by type
   */
  getItemsByType(type: ItemType): InventorySlot[] {
    return this.inventory.filter(slot => {
      if (slot.itemId === 0) return false;
      const item = getItem(slot.itemId);
      return item && item.type === type;
    });
  }

  /**
   * Get consumable items
   */
  getConsumables(): InventorySlot[] {
    return this.getItemsByType(ItemType.CONSUMABLE);
  }

  /**
   * Get equipment items (weapons, armor, etc.)
   */
  getEquipment(): InventorySlot[] {
    return this.inventory.filter(slot => {
      if (slot.itemId === 0) return false;
      const item = getItem(slot.itemId);
      return item && item.type !== ItemType.CONSUMABLE && item.type !== ItemType.KEY_ITEM;
    });
  }

  /**
   * Add item to inventory
   * Returns true if successful, false if inventory full
   */
  addItem(itemId: number, quantity: number = 1): boolean {
    const item = getItem(itemId);
    if (!item || quantity <= 0) return false;

    // For stackable items, try to add to existing stack
    if (item.stackable) {
      for (const slot of this.inventory) {
        if (slot.itemId === itemId && slot.quantity < InventorySystem.MAX_STACK) {
          const spaceInStack = InventorySystem.MAX_STACK - slot.quantity;
          const amountToAdd = Math.min(quantity, spaceInStack);
          slot.quantity += amountToAdd;
          quantity -= amountToAdd;

          if (quantity === 0) return true;
        }
      }
    }

    // Add to empty slot(s)
    while (quantity > 0) {
      const emptySlot = this.inventory.find(slot => slot.itemId === 0);
      if (!emptySlot) return false; // Inventory full

      const amountToAdd = item.stackable
        ? Math.min(quantity, InventorySystem.MAX_STACK)
        : 1;

      emptySlot.itemId = itemId;
      emptySlot.quantity = amountToAdd;
      quantity -= amountToAdd;
    }

    return true;
  }

  /**
   * Remove item from inventory
   * Returns true if successful, false if not enough items
   */
  removeItem(itemId: number, quantity: number = 1): boolean {
    if (quantity <= 0) return false;

    let remaining = quantity;

    for (const slot of this.inventory) {
      if (slot.itemId === itemId && remaining > 0) {
        const amountToRemove = Math.min(remaining, slot.quantity);
        slot.quantity -= amountToRemove;
        remaining -= amountToRemove;

        if (slot.quantity === 0) {
          slot.itemId = 0;
        }
      }
    }

    return remaining === 0;
  }

  /**
   * Check if inventory has item
   */
  hasItem(itemId: number, quantity: number = 1): boolean {
    const total = this.inventory
      .filter(slot => slot.itemId === itemId)
      .reduce((sum, slot) => sum + slot.quantity, 0);
    return total >= quantity;
  }

  /**
   * Get total quantity of an item
   */
  getItemCount(itemId: number): number {
    return this.inventory
      .filter(slot => slot.itemId === itemId)
      .reduce((sum, slot) => sum + slot.quantity, 0);
  }

  /**
   * Check if inventory is full
   */
  isFull(): boolean {
    return this.inventory.every(slot => slot.itemId !== 0);
  }

  /**
   * Get number of free slots
   */
  getFreeSlots(): number {
    return this.inventory.filter(slot => slot.itemId === 0).length;
  }

  /**
   * Sort inventory by item type, then by item ID
   */
  sort(): void {
    // Extract non-empty slots
    const items = this.getActiveSlots();

    // Sort by type, then by ID
    items.sort((a, b) => {
      const itemA = getItem(a.itemId);
      const itemB = getItem(b.itemId);

      if (!itemA || !itemB) return 0;

      if (itemA.type !== itemB.type) {
        return itemA.type - itemB.type;
      }

      return a.itemId - b.itemId;
    });

    // Clear inventory
    this.inventory.forEach(slot => {
      slot.itemId = 0;
      slot.quantity = 0;
    });

    // Place sorted items back
    items.forEach((item, index) => {
      this.inventory[index] = { ...item };
    });
  }

  /**
   * Compact inventory (merge stacks, remove empty slots)
   */
  compact(): void {
    const itemCounts = new Map<number, number>();

    // Count all items
    for (const slot of this.inventory) {
      if (slot.itemId > 0) {
        const current = itemCounts.get(slot.itemId) || 0;
        itemCounts.set(slot.itemId, current + slot.quantity);
      }
    }

    // Clear inventory
    this.inventory.forEach(slot => {
      slot.itemId = 0;
      slot.quantity = 0;
    });

    // Rebuild with optimal stacking
    let slotIndex = 0;
    for (const [itemId, totalQuantity] of itemCounts.entries()) {
      const item = getItem(itemId);
      if (!item) continue;

      let remaining = totalQuantity;
      const maxPerSlot = item.stackable ? InventorySystem.MAX_STACK : 1;

      while (remaining > 0 && slotIndex < InventorySystem.MAX_SLOTS) {
        const amountInSlot = Math.min(remaining, maxPerSlot);
        this.inventory[slotIndex] = {
          itemId,
          quantity: amountInSlot,
        };
        remaining -= amountInSlot;
        slotIndex++;
      }
    }
  }

  /**
   * Use a consumable item
   * Returns effect information for game logic to apply
   */
  useItem(itemId: number, inBattle: boolean): UseItemResult {
    const item = getItem(itemId);

    if (!item) {
      return { success: false, message: 'Item not found' };
    }

    if (item.type !== ItemType.CONSUMABLE) {
      return { success: false, message: 'Cannot use this item' };
    }

    if (!this.hasItem(itemId)) {
      return { success: false, message: 'You don\'t have this item' };
    }

    // Check if item can be used in current context
    if (inBattle) {
      // Some items can only be used outside battle
      if (itemId === 46 || itemId === 47) { // Tent, Sleeping Bag
        return { success: false, message: 'Cannot use outside of battle' };
      }
    }

    // Determine effect type
    let effect: UseItemResult['effect'];

    if (item.spellEffect > 0) {
      // Status cure items
      if (item.spellEffect === 4) { // Dead - Phoenix Down
        effect = {
          type: 'revive',
          value: item.power,
        };
      } else {
        effect = {
          type: 'cure_status',
          value: 0,
          statusEffect: item.spellEffect,
        };
      }
    } else if (item.power > 0) {
      // Healing items
      if (itemId === 46 || itemId === 47) { // Tent, Sleeping Bag
        effect = {
          type: 'party_heal',
          value: item.power,
        };
      } else if (itemId === 42) { // Ether
        effect = {
          type: 'restore',
          value: item.power,
        };
      } else if (itemId === 50) { // Elixir
        effect = {
          type: 'party_heal',
          value: 255, // Full restore
        };
      } else {
        effect = {
          type: 'heal',
          value: item.power,
        };
      }
    }

    // Remove item from inventory
    this.removeItem(itemId, 1);

    return {
      success: true,
      message: `Used ${item.name}`,
      effect,
    };
  }

  /**
   * Get slot index for an item
   */
  getSlotIndex(itemId: number): number {
    return this.inventory.findIndex(slot => slot.itemId === itemId);
  }

  /**
   * Clear inventory (for testing/reset)
   */
  clear(): void {
    this.inventory.forEach(slot => {
      slot.itemId = 0;
      slot.quantity = 0;
    });
  }

  /**
   * Debug: Print inventory contents
   */
  debug(): void {
    console.log('=== INVENTORY ===');
    this.getActiveSlots().forEach((slot, index) => {
      const item = getItem(slot.itemId);
      if (item) {
        console.log(`[${index}] ${item.name} x${slot.quantity}`);
      }
    });
    console.log(`Free slots: ${this.getFreeSlots()}`);
  }
}
